import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import { config, locales as zodLocales } from "zod"

import { IntlProvider } from "react-intl"
import enMessages from "../locales/en.json"
import { flattenMessages } from "../lib/i18n/flatten-messages"
import {
  getLocaleCookieDomain,
  localeCookieNames,
  normalizeLocale,
  resolveLocale,
} from "../lib/i18n/locale"
import jaMessages from "../locales/ja.json"
import thMessages from "../locales/th.json"
import viMessages from "../locales/vi.json"
import { useCookies } from "react-cookie"

export type Language = "en" | "ja" | "vi" | "th"

export const SUPPORTED_LANGUAGES: Language[] = ["en", "ja", "vi", "th"]

const ZOD_LOCALE_BY_LANG: Record<
  Language,
  () => ReturnType<typeof zodLocales.en>
> = {
  en: zodLocales.en,
  ja: zodLocales.ja,
  vi: zodLocales.vi,
  th: zodLocales.th,
}

interface LanguageContextType {
  language: Language
  setLanguage: (lang: Language) => void
  urlLanguage: Language | null
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
)

interface I18nProviderProps {
  children: ReactNode
}

const LANGUAGE_QUERY_KEY = "lang"

export function isSupportedLanguage(value: string | null): value is Language {
  return SUPPORTED_LANGUAGES.includes(value as Language)
}

function getUrlLanguage(): Language | null {
  if (typeof window === "undefined") return null
  const value = new URLSearchParams(window.location.search).get(
    LANGUAGE_QUERY_KEY
  )
  return isSupportedLanguage(value) ? value : null
}

function updateUrlLanguage(lang: Language) {
  if (typeof window === "undefined") return

  const url = new URL(window.location.href)
  if (!url.searchParams.has(LANGUAGE_QUERY_KEY)) return
  url.searchParams.set(LANGUAGE_QUERY_KEY, lang)
  window.history.replaceState(window.history.state, "", url)
}

export function I18nProvider({ children }: I18nProviderProps) {
  const [cookies, setCookie] = useCookies([...localeCookieNames])
  const [urlLanguage, setUrlLanguage] = useState<Language | null>(() =>
    getUrlLanguage()
  )
  const [selectedLanguage, setSelectedLanguage] = useState<Language>(() =>
    resolveLocale({
      urlLocale: urlLanguage,
      cookieLocales: localeCookieNames.map((name) => cookies[name]),
      browserLocale:
        typeof navigator !== "undefined" ? navigator.language : null,
    })
  )

  useEffect(() => {
    if (cookies.avacus_locale !== undefined) return

    setCookie("avacus_locale", selectedLanguage, {
      path: "/",
      maxAge: 31536000,
      sameSite: "lax",
      domain:
        typeof window !== "undefined"
          ? getLocaleCookieDomain(window.location.hostname)
          : undefined,
    })
  }, [cookies.avacus_locale, selectedLanguage, setCookie])

  useEffect(() => {
    const syncUrlLanguage = () => setUrlLanguage(getUrlLanguage())
    syncUrlLanguage()
    window.addEventListener("popstate", syncUrlLanguage)
    return () => window.removeEventListener("popstate", syncUrlLanguage)
  }, [setUrlLanguage])

  const language = urlLanguage ?? selectedLanguage

  useEffect(() => {
    const applyLocale = ZOD_LOCALE_BY_LANG[language]
    if (applyLocale) config(applyLocale())
  }, [language])

  const flattenedMessages = useMemo(() => {
    return {
      en: flattenMessages(enMessages),
      ja: flattenMessages(jaMessages),
      vi: flattenMessages(viMessages),
      th: flattenMessages(thMessages),
    }
  }, [])

  const handleSetLanguage = useCallback(
    (lang: Language) => {
      const locale = normalizeLocale(lang)
      if (!locale) return

      const cookieOptions = {
        path: "/",
        maxAge: 31536000,
        sameSite: "lax" as const,
        domain:
          typeof window !== "undefined"
            ? getLocaleCookieDomain(window.location.hostname)
            : undefined,
      }

      localeCookieNames.forEach((name) => {
        setCookie(name, locale, cookieOptions)
      })
      setSelectedLanguage(locale)
      if (getUrlLanguage()) setUrlLanguage(locale)
      updateUrlLanguage(locale)
    },
    [setCookie, setSelectedLanguage, setUrlLanguage]
  )

  const contextValue = useMemo(
    () => ({
      language,
      setLanguage: handleSetLanguage,
      urlLanguage,
    }),
    [language, handleSetLanguage, urlLanguage]
  )

  return (
    <LanguageContext.Provider value={contextValue}>
      <IntlProvider
        locale={language}
        messages={flattenedMessages[language]}
        defaultLocale="en"
      >
        {children}
      </IntlProvider>
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error("useLanguage must be used within I18nProvider")
  }
  return context
}